import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { GetAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import type { VariablesOf } from '~publish/graphql'
import NotificationActionCreators from '~publish/legacy/composer/composer/action-creators/NotificationActionCreators'
import { NotificationScopes } from '~publish/legacy/composer/composer/AppConstants'

type StartTrialInput = NonNullable<VariablesOf<typeof StartTrial>['input']>
type AttributionInput = NonNullable<StartTrialInput['attribution']>

export type UseStartTrialInput = {
  organizationId: string
  attribution: AttributionInput
}

export const StartTrial = graphql(/* GraphQL */ `
  mutation StartTrial($input: StartTrialInput!) {
    startTrial(input: $input) {
      __typename
      ... on VoidMutationError {
        message
      }
    }
  }
`)

export type StartTrial = (args: UseStartTrialInput) => Promise<void>

export const useStartTrial = (): StartTrial => {
  const openGlobalModal = useOpenGlobalModal()
  const [startTrial] = useMutation(StartTrial, {
    refetchQueries: [GetAccount],
    awaitRefetchQueries: true,
    // `onCompleted` waits for refetchQueries, so use `update` for optimistic notification cleanup.
    update: (_, result) => {
      if (result.data?.startTrial.__typename === 'StartTrialResponse') {
        NotificationActionCreators.removeAllNotificationsByScopePrefix(
          `${NotificationScopes.PROFILE_QUEUE_LIMIT}-`,
        )
      }
    },
    onCompleted: () => {
      window?.appshell?.actions?.refetchAccount?.()
    },
  })

  const startTrialCallback = useCallback(
    async ({
      organizationId,
      attribution,
    }: UseStartTrialInput): Promise<void> => {
      const { data, error } = await startTrial({
        variables: {
          input: {
            organizationId,
            attribution,
          },
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to start trial')
      }

      const result = data.startTrial
      if (result.__typename === 'VoidMutationError') {
        throw new Error(result.message ?? 'Failed to start trial')
      }

      if (result.__typename === 'StartTrialResponse') {
        openGlobalModal(MODALS.success, { startedTrial: true })
      }
    },
    [startTrial, openGlobalModal],
  )

  return startTrialCallback
}
